/**
 * Whether this learner is still in their first run, and what the app owes them next. F-141.
 *
 * Leo, on the sixteen minutes a research pass takes: *"nobody is going to wait 15 minutes for
 * something to happen, they'll go to a different tab or a different app, **or we ask them to do
 * things in the meantime**… the onboarding should take what it takes… **when they're done we know
 * they're reachable**."*
 *
 * So the wait is not a loading screen. It is when the product does its onboarding, and the one
 * thing onboarding has to come away with is a **reach window** — the setting without which nothing
 * this product does gets delivered. The owner was asked whether configuring it should be mandatory
 * and chose *no plan screen until configured*, which is why this module answers with a gate rather
 * than with a hint: `configure` is a state in which the plan screen is not reachable, deep link or
 * no deep link.
 *
 * **In a DOM-free module for `plan/step.ts`'s reason, and with one of its own.** The decision is
 * three facts wide and every one of its answers is a redirect, so getting it wrong does not look
 * like a bad screen — it looks like the app bouncing somebody between two addresses, which is the
 * failure `Landing` already carries a comment about. `packages/web/test/onboarding-gate.test.ts`
 * asserts it from plain Node; `App.tsx` holds the wiring, which nothing here can see.
 *
 * ---
 *
 * **This is a first-run flow, once per learner.** A learner stating their second objective does not
 * see it, and **the gate never closes for anybody who already has a reach window** — which is every
 * learner who exists on the day this ships. Both fall out of the rules below rather than out of a
 * version check.
 *
 * Note which half of that is guaranteed by what. The gate is derived from the API and is therefore
 * per session and per account by construction. The *tour* is derived from a mark in this browser's
 * storage, and a browser is not a person — so the mark is keyed by learner (`markKey`), because
 * under one shared key somebody walking the gate on a family laptop left a mark the next account
 * inherited, and got two example briefings above a thread three weeks old.
 *
 * ---
 *
 * **There is no per-plan version of this, and there cannot be one** (F-151 step 8). A gated learner
 * has exactly one plan: the gate closes only once an objective exists, and the slot cap is one
 * live plan for every tier anybody is on. So `SetupFacts` names an objective's status in the
 * singular deliberately — it is *the* plan, not a plan — and `/welcome` carries no `?o=`, because
 * the thing it acquires is the reach window, which is global. The tab bar and this flow never
 * appear together, and the reason is structural rather than lucky: tabs are what a learner gets
 * when they have a **second** plan, and this is what happens in the first minutes of their first.
 */

/** As much of the learner's setup as this decision needs. */
export interface SetupFacts {
  /** The current objective's status, or `none` when there is not one. */
  status: string;
  /** Whether a reach window has actually been saved. The defaults do not count. */
  windowConfigured: boolean;
  /** How far through the first run this browser has seen this learner get. See `OnboardingMark`. */
  mark?: OnboardingMark;
}

/**
 * How far through the first run this browser has seen this learner get.
 *
 * **Two values and not a boolean, because the absent case is the load-bearing one.** A learner who
 * has been here for a month carries nothing, and nothing has to mean *not in a first run* rather
 * than *has not finished one* — otherwise the samples would appear in the thread of everybody who
 * already knows what a briefing looks like, which is the opposite of what this is for.
 *
 * `configuring` is written when the gate closes in front of somebody, so it is only ever set for a
 * learner who genuinely arrived at an unconfigured product with an objective in flight.
 */
export type OnboardingMark = 'configuring' | 'done';

const MARKS: readonly string[] = ['configuring', 'done'];

/** The stored value, or nothing. Anything this does not recognise is nothing. */
export function asMark(value: unknown): OnboardingMark | undefined {
  return typeof value === 'string' && MARKS.includes(value) ? (value as OnboardingMark) : undefined;
}

/**
 * What the app owes this learner right now.
 *
 * `unknown` is not one of the answers a screen acts on. It is what absent facts produce, and it
 * exists so that a redirect is never rendered against a reading that has not arrived — sending
 * somebody to one screen and then moving them reads as the app deciding twice. `useSetupState` in
 * `App.tsx` carried that note until this feature replaced it; `FirstRunProvider` carries it now.
 */
export type FirstRun =
  /** The facts have not been read yet. Wait; do not redirect. */
  | 'unknown'
  /** Nothing owed. The app behaves exactly as it does for everybody else. */
  | 'open'
  /**
   * The gate is closed: there is an objective in flight and nowhere to deliver it.
   *
   * The plan screen is not reachable from here — the owner's decision, taken as a gate rather than
   * as a nag, and it is what makes *"when they're done we know they're reachable"* true rather than
   * hopeful.
   */
  | 'configure'
  /**
   * Reachable, and has not been shown what arrives.
   *
   * Step 3 of the flow: the chat, pre-filled with worked examples. **Offered and not enforced** —
   * the gate is about the reach window and nothing else, so everything is reachable again by the
   * time this is the answer.
   */
  | 'tour';

/**
 * The stage this learner is at.
 *
 * Three rules, in this order, and the order is the whole of it:
 *
 * 1. **No objective, no flow.** The plan screen is where an objective is stated, so gating it
 *    behind configuration would gate the only screen that can open the gate. Step 1 of the design
 *    is *state the objective*; this is what makes step 1 reachable.
 * 2. **An objective and no window is the gate.** Nothing else matters here — not the mark, not how
 *    far research has got. A learner who deep-links to `/plan` mid-flow lands back on the welcome
 *    screen, because the alternative is the sequence F-140's notice exists to apologise for.
 * 3. **The tour is owed only to somebody this browser watched walk through the gate.** That is the
 *    mark's whole job, and it is why a learner who already had a window never sees any of this.
 */
export function firstRun(facts: SetupFacts | undefined): FirstRun {
  if (!facts) return 'unknown';
  if (facts.status === 'none') return 'open';
  if (!facts.windowConfigured) return 'configure';
  return facts.mark === 'configuring' ? 'tour' : 'open';
}

/**
 * Whether the rest of the app is out of reach at this stage.
 *
 * One function rather than `stage === 'configure'` written at each call site, because the call
 * sites are a route guard and a redirect target and the two have to agree: a guard that sends
 * somebody to a screen the guard would itself bounce is an infinite redirect, and that is the
 * shape this answer exists to make impossible to write by accident.
 */
export function gateIsClosed(stage: FirstRun): boolean {
  return stage === 'configure';
}

/**
 * Where the browser remembers how far through the first run this learner got.
 *
 * **Per device, deliberately, and it is the weaker of the two halves.** The gate itself is derived
 * from the API — the objective's status and whether a window has been saved — so the promise that
 * matters ("when they're done we know they're reachable") is held by storage nobody can clear. This
 * marks only whether the *illustrations* have been shown, and the worst a cleared browser can do is
 * show a learner two example bubbles a second time, above an empty thread, with a button that
 * dismisses them. Putting it on the learner's row would cost an endpoint, a write and a migration
 * for that.
 */
export const ONBOARDING_KEY = 'nabu.onboarding';

/**
 * The key this learner's mark is stored under.
 *
 * **Per learner, because a browser is not a person.** A single key meant that learner A walking
 * through the gate on a shared laptop left a `configuring` mark that learner B then inherited: B
 * has a reach window and a live objective, `firstRun` answers `tour`, and somebody three weeks into
 * the product gets two example briefings above their real thread. That contradicts the one rule
 * this feature states everywhere — *a learner who already has a reach window never sees this flow*
 * — which held for the gate (derived from the API, per session) and leaked through the tour.
 *
 * Keyed rather than cleared on sign-out for two reasons: signing out is not the only way accounts
 * change hands, and a key that has to be cleared somewhere else is a key that will one day not be.
 *
 * An unknown learner gets a key nothing is ever written under, so they get no tour. That is the
 * right direction to be wrong in: the cost of a missing tour is that somebody reads their first
 * real briefing without a worked example, and the cost of a wrong one is showing examples to
 * somebody who has been using the product for a month.
 */
export function markKey(userId: string | undefined): string {
  return `${ONBOARDING_KEY}:${userId ?? 'unknown'}`;
}

/**
 * The mark this browser holds for this learner, or nothing.
 *
 * Wrapped, because a browser with site data blocked **throws** on the read rather than answering
 * null — the same hazard `initialLocale` in `i18n.ts` is wrapped for. A throw here would take the
 * whole gate down with it, and the gate is the one decision in this file that must never fail
 * closed against somebody who is already configured.
 */
export function readMark(userId: string | undefined): OnboardingMark | undefined {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    return asMark(localStorage.getItem(markKey(userId)));
  } catch {
    // No storage. The learner is treated as having no mark, which means the gate still holds
    // (it is derived from the API) and the samples are simply never offered.
    return undefined;
  }
}

/** Remembers how far they got. A failure is a forgotten tour, never a broken screen. */
export function writeMark(mark: OnboardingMark, userId: string | undefined): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(markKey(userId), mark);
  } catch {
    // See `readMark`. Nothing downstream depends on this having worked.
  }
}
